import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "../../authentication/AuthContext";

// ── Where a user's column choices live on the client ──────────────────────────
//
// One fetch of GET /api/table_preferences per session, held here, read by every
// listing page. The alternative — each page fetching its own listing on mount —
// would put a round trip in front of the first paint of every table, and the
// whole payload for a user is a few hundred bytes.
//
// ── Why the whole set, and why once ──────────────────────────────────────────
//
// Preferences only change when this same user saves them, and a save updates the
// cache in place. There is no other writer, so there is nothing to poll for and
// no staleness to detect — unlike permissions, which another admin can change
// underneath you (see the x-permissions-version machinery in AuthContext).
//
// ── Rendering before the fetch lands ─────────────────────────────────────────
//
// `ready` is false until the fetch settles. Pages hold the table's render until
// then, because showing default columns and then re-laying-out to the user's
// saved set is a visible jump. If the request FAILS, `ready` still flips to true
// with an empty cache: a preferences outage degrades to "everyone sees the
// default columns", never to a page that won't render.
//
// ── Why this waits for AuthContext ───────────────────────────────────────────
//
// The fetch is keyed off the auth status rather than off the token sitting in
// localStorage, for two reasons. AuthProvider installs its axios interceptor in
// an effect, and React runs a child's effects BEFORE its parent's — so a fetch
// issued from this provider's own mount effect would go out before the
// refresh-and-retry interceptor existed, and a merely-expired token would look
// like an outage. And keying on identity is what makes a second user signing in
// (without the full page reload that logout does) fetch their own preferences
// instead of inheriting the first user's.

const EMPTY = {};

const TablePreferencesContext = createContext({
  ready: true,
  preferences: EMPTY,
  getPreference: () => null,
  savePreference: async () => {},
  resetPreference: async () => {},
});

export const useTablePreferences = () => useContext(TablePreferencesContext);

export function TablePreferencesProvider({ children }) {
  const [preferences, setPreferences] = useState(EMPTY);
  const [ready, setReady] = useState(false);

  const { status, email } = useAuth();

  const apiUrl = process.env.REACT_APP_API_URL;

  // Which identity the cache currently holds preferences for. Guards against a
  // re-render re-issuing the fetch, and is what a sign-in as a different user
  // compares unequal against.
  const fetchedForRef = useRef(null);

  const authHeaders = useCallback(() => ({
    "access-token": localStorage.getItem("accessToken"),
  }), []);

  useEffect(() => {
    // Still verifying the token — say nothing yet, so pages keep their loader up
    // rather than painting default columns they may be about to replace.
    if (status === "loading") return;

    if (status !== "ready") {
      // Signed out (or never signed in). Drop anything cached so the next user to
      // sign in on this tab cannot see the previous one's layout, and let tables
      // render their defaults.
      fetchedForRef.current = null;
      setPreferences(EMPTY);
      setReady(true);
      return;
    }

    const identity = email || "unknown";
    if (fetchedForRef.current === identity) return;
    fetchedForRef.current = identity;
    setReady(false);

    let cancelled = false;
    axios
      .get(`${apiUrl}/table_preferences`, {
        headers: { "access-token": localStorage.getItem("accessToken") },
      })
      .then((res) => {
        if (cancelled) return;
        setPreferences(res.data?.status && res.data.preferences ? res.data.preferences : EMPTY);
      })
      .catch(() => {
        // Silent by design. A failed preferences fetch is not something the user
        // asked for or can act on, and the table still works without it. Clear the
        // marker so a later re-render gets another attempt.
        if (!cancelled) fetchedForRef.current = null;
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, status, email]);

  // The ordered column ids this user chose for a listing, or null when they never
  // customised it. Null and [] are NOT interchangeable: null means "use the page
  // defaults", while [] would mean "show no columns" (which the API refuses to
  // store, so it should never arrive).
  const getPreference = useCallback((listingKey) => {
    const value = preferences[listingKey];
    return Array.isArray(value) && value.length ? value : null;
  }, [preferences]);

  const savePreference = useCallback(async (listingKey, columns) => {
    const res = await axios.put(
      `${apiUrl}/table_preferences/${encodeURIComponent(listingKey)}`,
      { columns },
      { headers: authHeaders() }
    );
    if (!res.data?.status) {
      throw new Error(res.data?.message || "Could not save table layout.");
    }
    // Trust the server's echo rather than the local array: it has de-duplicated
    // and trimmed the ids, so this keeps the cache identical to what a fresh
    // fetch would return.
    const saved = Array.isArray(res.data.columns) ? res.data.columns : columns;
    setPreferences((prev) => ({ ...prev, [listingKey]: saved }));
    return saved;
  }, [apiUrl, authHeaders]);

  const resetPreference = useCallback(async (listingKey) => {
    const res = await axios.delete(
      `${apiUrl}/table_preferences/${encodeURIComponent(listingKey)}`,
      { headers: authHeaders() }
    );
    if (!res.data?.status) {
      throw new Error(res.data?.message || "Could not reset table layout.");
    }
    // Drop the key entirely rather than setting it to [] — absence is what the
    // rest of the code reads as "fall back to the page defaults".
    setPreferences((prev) => {
      const next = { ...prev };
      delete next[listingKey];
      return next;
    });
  }, [apiUrl, authHeaders]);

  return (
    <TablePreferencesContext.Provider
      value={{ ready, preferences, getPreference, savePreference, resetPreference }}
    >
      {children}
    </TablePreferencesContext.Provider>
  );
}

export default TablePreferencesContext;
